import React, { useState, useEffect } from 'react';
import { DateRange } from 'react-date-range';
import { FormControl, MenuItem, Select, Box } from '@mui/material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const CustomDateRange = ({ onRangeChange, initialRange }) => {
  const today = new Date();
  const last30Days = new Date();
  last30Days.setDate(today.getDate() - 30);

  const [range, setRange] = useState(initialRange || [{ startDate: last30Days, endDate: today, key: 'selection' }]);

  const currentMonth = range[0]?.startDate?.getMonth() || new Date().getMonth();
  const currentYear = range[0]?.startDate?.getFullYear() || new Date().getFullYear();

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const years = Array.from({ length: 30 }, (_, i) => currentYear - 15 + i);

  const handleRangeChange = (item) => {
    const newRange = [item.selection];
    setRange(newRange);
    // Call parent callback to update filters
    if (onRangeChange) {
      onRangeChange(item.selection);
    }
  };

  const handleMonthChange = (month) => {
    if (!range[0]?.startDate || !range[0]?.endDate) return;
    
    const start = new Date(range[0].startDate);
    start.setMonth(month);
    const end = new Date(range[0].endDate);
    const newRange = [{ startDate: start, endDate: end, key: 'selection' }];
    setRange(newRange);
    // Call parent callback to update filters
    if (onRangeChange) {
      onRangeChange({ startDate: start, endDate: end });
    }
  };

  const handleYearChange = (year) => {
    if (!range[0]?.startDate || !range[0]?.endDate) return;
    
    const start = new Date(range[0].startDate);
    start.setFullYear(year);
    const end = new Date(range[0].endDate);
    const newRange = [{ startDate: start, endDate: end, key: 'selection' }];
    setRange(newRange);
    // Call parent callback to update filters
    if (onRangeChange) {
      onRangeChange({ startDate: start, endDate: end });
    }
  };

  useEffect(() => {
    console.log('Updated range:', range);
  }, [range]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* ✅ Centered Month + Year Selectors */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 1,
          justifyContent: 'center' // 👈 Center horizontally
        }}
      >
        <FormControl
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'end' // 👈 Center horizontally
          }}
          size="small"
        >
          <Box component="label" sx={{ fontSize: 12, fontWeight: 500, color: '#374151', mb: 0.5 }}>
            Month
          </Box>
          <Select
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            MenuProps={{ disablePortal: true }}
            value={currentMonth}
            onChange={(e) => handleMonthChange(Number(e.target.value))}
            sx={{
              border: '1px solid #c4c4c4',
              minWidth: 100,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#644ee5 !important',
                borderWidth: '2px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
            }}
          >
            {months.map((m, idx) => (
              <MenuItem
                key={m}
                value={idx}
                sx={{
                  '&:hover': { background: '#ede9fe', color: '#111827' },
                  '&.Mui-selected': { background: '#644ee5', color: '#fff' }
                }}
              >
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'start' // 👈 Center horizontally
          }}
          size="small"
        >
          <Box component="label" sx={{ fontSize: 12, fontWeight: 500, color: '#374151', mb: 0.5 }}>
            Year
          </Box>
          <Select
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                style: {
                  maxHeight: 384 // 👈 limit height of the dropdown
                }
              }
            }}
            value={currentYear}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            sx={{
              minWidth: 100,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#644ee5 !important',
                borderWidth: '2px'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#644ee5' }
            }}
          >
            {years.map((y) => (
              <MenuItem
                key={y}
                value={y}
                sx={{
                  '&:hover': { background: '#ede9fe', color: '#111827' },
                  '&.Mui-selected': { background: '#644ee5', color: '#fff' }
                }}
              >
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ✅ Calendar stays below */}
      <DateRange
        onChange={handleRangeChange}
        moveRangeOnFirstSelection={false}
        months={2}
        direction="horizontal"
        ranges={range}
        rangeColors={['#644ee5']}
        showDateDisplay={true}
        dateDisplayFormat="dd-MM-yyyy"
        showMonthAndYearPickers={false}
        showMonthArrow
      />
    </Box>
  );
};

export default CustomDateRange;
